'use client';

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';

const CSRFContext = createContext();

export const CSRFProvider = ({ children }) => {
  const [csrfToken, setCsrfToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCSRFToken = useCallback(async () => {
    setLoading(true);
    let timeoutId;
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch('/api/auth/csrf', {
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include', // Include cookies for session
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      if (response.ok) {
        const data = await response.json();
        setCsrfToken(data.csrfToken);
      } else {
        // Only log if it's not a network error (which is expected in some cases)
        if (response.status !== 0) {
          console.warn('Failed to fetch CSRF token:', response.status);
        }
        setCsrfToken(null);
      }
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      // Suppress network errors that are likely from browser extensions or offline state
      if (error.name === 'AbortError' || error.name === 'TypeError') {
        // Network errors are expected in some scenarios (extensions, offline, etc.)
        // Don't spam console with these
        setCsrfToken(null);
      } else {
        console.error('Error fetching CSRF token:', error);
        setCsrfToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCSRFToken();
  }, [fetchCSRFToken]);

  const value = { csrfToken, loading, refreshCSRF: fetchCSRFToken };

  return (
    <CSRFContext.Provider value={value}>
      {children}
    </CSRFContext.Provider>
  );
};

export const useCSRF = () => {
  const context = useContext(CSRFContext);
  if (!context) {
    throw new Error('useCSRF must be used within a CSRFProvider');
  }
  return context;
}; 